import { AlertTriangle, CheckCircle2, Clock, History, MessageCircleReply } from "lucide-react";

import type { SendResult, TeaserResult } from "@/types/draft";
import { SendLabels } from "@/lib/labels";
import { cn } from "@/lib/utils";

const windowText: Record<SendResult["window"], string> = {
	inside: "dentro da janela de 24h",
	outside: "fora da janela de 24h",
	unknown: "janela desconhecida",
};

export function SendResultView({ result }: { result: SendResult }) {
	const blocked = result.outcome === "blocked";

	const message = result.block_reason
		? SendLabels.blockReason[result.block_reason]
		: SendLabels.outcome[result.outcome];

	return (
		<div className="flex flex-col gap-1.5">
			<div
				data-component="send-result"
				data-outcome={result.outcome}
				data-mode={result.mode}
				data-repeat={result.repeat}
				data-block-reason={result.block_reason ?? ""}
				className={cn(
					"flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs",
					blocked
						? "border-destructive/30 bg-destructive/[0.07] text-destructive"
						: "border-primary/30 bg-primary/[0.06] text-primary",
				)}
			>
				{blocked && <AlertTriangle className="size-3.5 shrink-0" />}
				{result.outcome === "pending" && <Clock className="size-3.5 shrink-0" />}
				{!blocked && result.outcome !== "pending" && <CheckCircle2 className="size-3.5 shrink-0" />}
				{message}
			</div>

			{!blocked && (
				<p
					data-slot="result-footer"
					className="flex items-center gap-2 text-xs text-muted-foreground [&>*+*]:border-l [&>*+*]:border-border [&>*+*]:pl-2"
				>
					<span className="font-mono">#{result.id}</span>
					<span>{result.channel}</span>
					<span>{windowText[result.window]}</span>
				</p>
			)}

			{result.repeat && (
				<p
					data-slot="repeat-note"
					className="flex items-center gap-1.5 text-xs text-muted-foreground"
				>
					<History className="size-3.5 shrink-0" />
					{SendLabels.repeat}
				</p>
			)}
		</div>
	);
}

export function TeaserResultView({ result }: { result: TeaserResult }) {
	const blocked = result.outcome === "blocked";
	const message = result.block_reason
		? SendLabels.teaserBlockReason[result.block_reason]
		: SendLabels.teaserOutcome[result.outcome];

	return (
		<div
			data-component="teaser-result"
			data-outcome={result.outcome}
			data-mode={result.mode}
			data-block-reason={result.block_reason ?? ""}
			className={cn(
				"flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs",
				blocked
					? "border-destructive/30 bg-destructive/[0.07] text-destructive"
					: "border-warning/40 bg-warning/10 text-warning",
			)}
		>
			{blocked && <AlertTriangle className="size-3.5 shrink-0" />}
			{!blocked && <MessageCircleReply className="size-3.5 shrink-0" />}
			{message}
		</div>
	);
}
